import { format } from "date-fns";
import { CornerDownRight } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import { AuditActionType, type EntityHistoryItem } from "@/api/admin/auditLog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { commonIconProps14 } from "@/utils/constants";

import { BageColorMap, IconMap, NodeColorMap } from "./constants";
import {
  type FieldMapping,
  type FieldResolver,
  getChanges,
} from "./utils/getChanges";
import {
  getHistoryTitle,
  type HistoryTitleResolver,
} from "./utils/getHistoryTitle";
import { getInitials } from "./utils/getInitials";

const isEmptyHistoryValue = (value: ReactNode) =>
  value === null || value === undefined || value === "" || value === "—";

export const HistoryCard = ({
  log,
  forceExpanded,
  mapKeyToName,
  fieldResolvers,
  includeEntityNameInTitle,
  resolveHistoryTitle,
}: {
  log: EntityHistoryItem;
  forceExpanded?: boolean;
  mapKeyToName?: FieldMapping;
  fieldResolvers?: Record<string, FieldResolver>;
  includeEntityNameInTitle?: boolean;
  resolveHistoryTitle?: HistoryTitleResolver;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const changes = getChanges(log, mapKeyToName, fieldResolvers);
  const showFieldChanges =
    (log.action === AuditActionType.UPDATE ||
      log.action === AuditActionType.CREATE) &&
    changes.length > 0;

  useEffect(() => {
    setIsOpen(!!forceExpanded);
  }, [forceExpanded]);

  return (
    <div className="relative pl-10">
      <div className="absolute left-[15px] top-0 bottom-0 w-px bg-border" />

      <div
        className={cn(
          "absolute left-0 top-3 flex h-8 w-8 items-center justify-center rounded-full",
          NodeColorMap[log.action]
        )}
      >
        {IconMap[log.action]}
      </div>

      <Accordion
        type="single"
        collapsible
        value={isOpen ? "history" : ""}
        onValueChange={val => setIsOpen(val === "history")}
      >
        <AccordionItem value="history" className="rounded-xl border px-4">
          <AccordionTrigger className="hover:no-underline cursor-pointer">
            <div className="flex w-full items-center gap-3 pr-3">
              <div className="flex-1 text-left">
                <span className="font-bold">
                  {getHistoryTitle(
                    log,
                    mapKeyToName,
                    fieldResolvers,
                    includeEntityNameInTitle,
                    resolveHistoryTitle
                  )}
                </span>
              </div>

              <div className="flex items-center gap-4">
                <Badge
                  variant="outline"
                  className={cn("font-semibold", BageColorMap[log.action])}
                >
                  {log.action}D
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {format(new Date(log.timestamp), "hh:mm a")}
                </span>
              </div>
            </div>
          </AccordionTrigger>

          <AccordionContent className="border-t pt-4">
            {showFieldChanges ? (
              <div className="space-y-3">
                {changes.map(change => (
                  <div key={change.field} className="flex flex-col gap-2">
                    <div className="w-fit flex gap-1 text-gray-400 text-base flex-wrap items-start">
                      <div className="shrink-0">{change.fieldLabel}:</div>
                      <del className="whitespace-pre-wrap">
                        {isEmptyHistoryValue(change.before)
                          ? "—"
                          : change.before}
                      </del>
                    </div>

                    <div className="flex items-start gap-2 font-bold text-base pl-1">
                      <div className="flex items-center gap-2 font-bold shrink-0">
                        <CornerDownRight {...commonIconProps14} />
                        {!isEmptyHistoryValue(change.after) && (
                          <span className="h-2 w-2 rounded-full bg-orange-500" />
                        )}
                      </div>
                      <div className="whitespace-pre-wrap">
                        {isEmptyHistoryValue(change.after) ? "—" : change.after}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              log.action !== AuditActionType.UPDATE && (
                <div className="text-sm text-muted-foreground">
                  {log.entityType} {log.action.toLowerCase()}d
                </div>
              )
            )}

            <div className="flex items-center gap-1 mt-4">
              <div className="w-8 h-8 rounded-full bg-[#618829] mr-1 text-white flex items-center justify-center text-xs font-semibold">
                {getInitials(log?.username, log?.isSystem)}
              </div>
              <span className="text-xs text-muted-foreground">by</span>
              <span className="text-xs font-bold">
                {log?.isSystem ? "System" : log?.username}
              </span>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
